/**
 * One finding, as every review surface shows it: severity, title, the items it
 * names, the summary and the expandable detail, Accept and Dismiss while it
 * waits, and Reopen for whoever reviewed it. Used by the generic agent report
 * page's findings list (components/agents/outputs/FindingsReview.tsx).
 */

import { useState } from 'react';
import { formatDate } from '../../../i18n/formatters';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Check, X, AlertTriangle } from 'lucide-react';
import { LinkifiedText } from '../../prioritization/LinkifiedText';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import type { AgentFinding, FindingSeverity } from '../../../lib/agentFindingsService';
import { ReverseActionButton } from '../../reversals/ReverseActionButton';
import { reopenAgentFinding } from '../../../lib/reversals/reversalsService';

// ── Severity styling ─────────────────────────────────────────────────────────────

const SEVERITY_STYLE: Record<FindingSeverity, { label: string; chip: string; icon: boolean }> = {
  critical: { label: 'Critical', chip: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]', icon: true },
  high:     { label: 'High',     chip: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]', icon: true },
  medium:   { label: 'Medium',   chip: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]', icon: false },
  low:      { label: 'Low',      chip: 'bg-[var(--hover-tint)] text-[var(--text-sec)] border-[var(--rule)]', icon: false },
  info:     { label: 'Info',     chip: 'bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]', icon: false },
};

const DAY: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
const day = (iso?: string | null) => (iso ? formatDate(iso, DAY) || '—' : '—');

// ── Item title resolver (reuses items store cache) ───────────────────────────────

function useItemTitles(itemIds: string[]): Record<string, string> {
  const items = useItemsStore((s) => s.items);
  const result: Record<string, string> = {};
  for (const id of itemIds) {
    const item = items.find((i) => i.id === id);
    result[id] = item?.title ?? `Item ${id.slice(0, 8)}…`;
  }
  return result;
}

// ── Finding card ─────────────────────────────────────────────────────────────────

interface FindingCardProps {
  finding: AgentFinding;
  typeLabel: string;
  onAccept: (id: string) => Promise<void>;
  onDismiss: (id: string) => Promise<void>;
  isProcessing: boolean;
  readOnly?: boolean;
  /** Current user — the reversal is only offered to whoever reviewed the finding. */
  userId?: string | null;
  onReopened?: () => void;
}

export function FindingCard({ finding, typeLabel, onAccept, onDismiss, isProcessing, readOnly, userId, onReopened }: FindingCardProps) {
  const [open, setOpen] = useState(false);
  const titles = useItemTitles(finding.itemIds);
  const sev = SEVERITY_STYLE[finding.severity];

  return (
    <div className="card space-y-3">
      <div className="flex items-start gap-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${sev.chip} flex-shrink-0 mt-0.5`}>
          {sev.icon && <AlertTriangle className="h-3 w-3" />}
          {sev.label}
        </span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-sec)] flex-shrink-0 mt-0.5">
          {typeLabel}
        </span>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[var(--text-pri)] leading-snug">{finding.title}</p>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">detected {day(finding.createdAt)}</p>
        </div>

        {!readOnly && finding.status === 'active' && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => void onAccept(finding.id)}
              disabled={isProcessing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)] hover:bg-[var(--green-dim)] disabled:opacity-50 transition-colors"
              title="Acknowledge — this is worth acting on"
            >
              <Check className="h-3.5 w-3.5" />
              Accept
            </button>
            <button
              onClick={() => void onDismiss(finding.id)}
              disabled={isProcessing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--surface)] text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--hover-tint)] disabled:opacity-50 transition-colors"
              title="Dismiss — not a concern"
            >
              <X className="h-3.5 w-3.5" />
              Dismiss
            </button>
          </div>
        )}

        {readOnly && finding.status !== 'active' && (
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
              finding.status === 'accepted' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]' : 'bg-[var(--surface)] text-[var(--text-ter)] border-[var(--rule)]'
            }`}>
              {finding.status === 'accepted' ? <><Check className="h-3 w-3" /> Accepted</> : <><X className="h-3 w-3" /> Dismissed</>}
            </span>
            {userId && finding.reviewedByUserId === userId && (
              <ReverseActionButton
                label="Reopen"
                confirmTitle="Reopen finding"
                explain={`This will return "${finding.title}" to the active inbox for review again.`}
                run={(reason) => reopenAgentFinding(finding.id, reason)}
                onDone={onReopened}
                variant="ghost"
                size="sm"
                className="!px-2 !py-1 !text-xs"
              />
            )}
          </div>
        )}
      </div>

      {/* Summary — always visible */}
      {finding.summary && <LinkifiedText text={finding.summary} className="text-sm text-[var(--text-sec)] leading-relaxed" />}

      {/* Detail + items — expandable */}
      {(finding.detail || finding.itemIds.length > 0) && (
        <div>
          <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {open ? 'Hide' : 'Show'} detail
          </button>
          {open && (
            <div className="mt-2 space-y-3">
              {finding.detail && <LinkifiedText text={finding.detail} className="text-sm text-[var(--text-sec)] leading-relaxed" />}
              {finding.itemIds.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-[var(--text-ter)] mb-1">Items involved</p>
                  <ul className="space-y-1">
                    {finding.itemIds.map((id) => (
                      <li key={id}>
                        <Link to={`/prioritization/items/${id}`} className="text-sm text-primary-600 hover:text-primary-800 hover:underline">
                          {titles[id]}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {readOnly && finding.reviewedAt && (
        <p className="text-xs text-[var(--text-ter)]">
          {finding.status === 'accepted' ? 'Accepted' : 'Dismissed'} on {day(finding.reviewedAt)}
        </p>
      )}
    </div>
  );
}

